import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { withLazyLoad, useLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';
import ContentSection from '~shared/components/react/ContentSection';
import BetCardsList from '~shared/components/react/BetCardsList';

import { getMarketHeader } from '~shared/utils/widgets.js';
import { getDisplayDate } from '~shared/utils/date-time.js';

import {
    selectMarketSwitcherSelectionTypeIds,
    selectMarketSwitcherSpecialOdds,
} from '~shared/components/react/MetaDataStore/selectors';
import { filterEventsSelections } from '../utils';

/**
 * Render single tournament and it's events with lazy rendering
 * @param {Object} props props
 * @returns {JSX} react component
 */
const TournamentEventsSection = ({
    sportSlug,
    tournamentSlug,
    eventsList: rawEventsList,
    tournamentSectionParams,
    showDateHeader = false,
    showTournamentHeader = true,
    marketHeaders = null,
}) => {
    const { lazyLoadHasLoaded } = useLazyLoadContext();
    useEffect(() => {
        lazyLoadHasLoaded();
    }, []);

    const selectionTypeIds = useSelector(selectMarketSwitcherSelectionTypeIds);
    const specialOddsValue = useSelector(selectMarketSwitcherSpecialOdds);

    // remove events with no markets - event may have ended
    const eventsList = rawEventsList.filter(({ markets = null }) => (markets !== null));
    if (eventsList.length === 0) {

        return null;
    }

    // filter selections - if we added an event via ws inplay_start, we'll have registered at
    // market level. For O/U markets or markets where we only display a subset of selections
    // (e.g. match result and btts) we only display a subset of the market selections
    const filteredEvents = filterEventsSelections({
        events: eventsList,
        selectionTypeIds,
        specialOddsValue,
    });

    const [{
        markets = {},
        market_id,
        start_time,
        usesThreeMarketView,
    }] = filteredEvents;

    const marketsData = markets[market_id] || {};
    const formattedDate = showDateHeader ? getDisplayDate(start_time) : '';

    const headerText = marketHeaders
        ? [formattedDate, ...marketHeaders] // from marketswitcher/props
        : getMarketHeader(sportSlug, formattedDate, marketsData, usesThreeMarketView); // from page load

    const eventSectionParams = {
        headerText,
        classList: ['eventList__content-section'],
        isCollapsible: false,
        titleModifiers: ['column-headers'],
    };

    const betButtonBaseGtmAttribute = `sports-page;${sportSlug};`;

    if (showTournamentHeader) {
        return (
            <ContentSection key={tournamentSlug} params={tournamentSectionParams}>
                <ContentSection params={eventSectionParams}>
                    <BetCardsList events={filteredEvents} gtmAttribute={betButtonBaseGtmAttribute} />
                </ContentSection>
            </ContentSection>
        );
    }

    return (
        <ContentSection params={eventSectionParams}>
            <BetCardsList events={filteredEvents} gtmAttribute={betButtonBaseGtmAttribute} />
        </ContentSection>
    );
};

TournamentEventsSection.propTypes = {
    eventsList: PropTypes.arrayOf(PropTypes.object).isRequired,
    sportSlug: PropTypes.string.isRequired,
    tournamentSlug: PropTypes.string,
    tournamentSectionParams: PropTypes.shape({
        headerText: PropTypes.string,
        classList: PropTypes.arrayOf(PropTypes.string),
        isCollapsible: PropTypes.bool,
        seeMore: PropTypes.shape({
            sportSlug: PropTypes.string,
            tournamentSlug: PropTypes.string,
            categorySlug: PropTypes.string,
            tournamentId: PropTypes.number,
        }),
    }),
    showDateHeader: PropTypes.bool,
    showTournamentHeader: PropTypes.bool,
    marketHeaders: PropTypes.arrayOf(PropTypes.string),
};

const TournamentEventsSectionLazy = withLazyLoad(200)(TournamentEventsSection);

export default TournamentEventsSectionLazy;
